import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Form, Table } from "react-bootstrap";

import ErrorComponent from "@/modules/shared/ui/ErrorComponent";
import { translate } from "@/modules/shared/IntlProviderWrapper";
import Loader from "@/modules/shared/ui/Loader";
import Pagination from "@/modules/shared/ui/Pagination";
import SimpleLink from "@/modules/shared/ui/SimpleLink";
import { getBackend as getBackend2 } from "@/lib/backend/backend2";
import type {
    IGetLatestModelEvaluationKpiAggregationsRes,
    IModelEvaluationKpiModelRow,
} from "@/lib/backend/reports.generated.types";
import type { ILineData } from "@/lib/backend/manufacturing2.generated.types";
import { getLineGroupsForUser, getPlantsForUser, waitForManufacturingAsync } from "@/lib/businessLogic";
import {
    buildEntityLabelMaps,
    buildModelsExportRequest,
    buildModelsQueryRequest,
    buildModelBrowserUrl,
    clampModelsPageIndex,
    DEFAULT_MODEL_EVALUATION_MODELS_PAGE_SIZE,
    DEFAULT_MODEL_EVALUATION_MODELS_SORT,
    DEFAULT_MODEL_EVALUATION_MODELS_TAB_FILTERS,
    exportModelsCsv,
    fetchModelsPage,
    formatCountWithPct,
    formatDurationHours,
    formatErrorRatioPct,
    formatMaterialLabel,
    mergeHandoffIntoModelsFilters,
    modelEvaluationJobUuidMismatch,
    modelsFiltersAreDefault,
    resolveModelBrowserExternalIds,
    type ModelEvaluationModelsFilters,
    type ModelEvaluationModelsFiltersState,
    type ModelEvaluationModelsSort,
    type ModelEvaluationModelsSortKey,
    type ModelEvaluationWindowKey,
    toggleModelsSort,
} from "@/lib/ModelEvaluationKpis";
import { createCsvAndDownload } from "@/lib/util";

import WinMixBar from "./WinMixBar";
import ModelEvaluationFilterBar from "./ModelEvaluationFilterBar";
import { SortableKpiTh, WinMixColumnHeader, sortDirectionIndicator } from "./modelEvaluationTable";

type Props = {
    window: ModelEvaluationWindowKey;
    aggregations: IGetLatestModelEvaluationKpiAggregationsRes;
    handoffFilters: ModelEvaluationModelsFilters;
    handoffNonce: number;
};

const PAGE_SIZES = [25, 50, 100];

function normalizeMaterialExternalIdInput(value: string): string | null {
    const trimmed = value.trim();
    if (trimmed === "") {
        return null;
    }
    return trimmed;
}

function ModelsTable(props: {
    rows: IModelEvaluationKpiModelRow[];
    sort: ModelEvaluationModelsSort;
    label_maps: ReturnType<typeof buildEntityLabelMaps>;
    line_external_id_by_line_id: Map<number, string>;
    onSortChange: (sort: ModelEvaluationModelsSort) => void;
}) {
    const sortableTh = (key: ModelEvaluationModelsSortKey, label: string, numeric = false) => (
        <SortableKpiTh
            sort_key={key}
            label={label}
            numeric={numeric}
            onClick={() => {
                props.onSortChange(toggleModelsSort(props.sort, key));
            }}
            indicator={sortDirectionIndicator(props.sort.key, key, props.sort.dir)}
        />
    );

    return (
        <div className="table-responsive model-evaluation-table">
            <Table size="sm" striped hover className="dashboard-table mb-0">
                <thead className="table-header-neutral">
                    <tr>
                        <th scope="col">{translate("Manufacturing.ModelEvaluation.table.column.plant", "Plant")}</th>
                        {sortableTh("line_id", translate("Manufacturing.ModelEvaluation.table.column.line", "Line"))}
                        {sortableTh(
                            "material_id",
                            translate("Manufacturing.ModelEvaluation.table.column.material", "Material")
                        )}
                        {sortableTh(
                            "process_num",
                            translate("Manufacturing.ModelEvaluation.table.column.process", "Process"),
                            true
                        )}
                        {sortableTh(
                            "operations_count",
                            translate("Manufacturing.ModelEvaluation.table.column.operations", "Operations"),
                            true
                        )}
                        <th scope="col" className="text-right">
                            {translate("Manufacturing.ModelEvaluation.kpi.total_duration", "Total duration (h)")}
                        </th>
                        <th scope="col" className="text-right">
                            {translate("Manufacturing.ModelEvaluation.kpi.model_wins", "Model wins")}
                        </th>
                        <th scope="col" className="text-right">
                            {translate("Manufacturing.ModelEvaluation.kpi.normative_wins", "Norm wins")}
                        </th>
                        <th scope="col" className="text-right">
                            {translate("Manufacturing.ModelEvaluation.kpi.fallback", "Fallback")}
                        </th>
                        <WinMixColumnHeader />
                        {sortableTh(
                            "avg_model_error_ratio_pct",
                            translate("Manufacturing.ModelEvaluation.table.column.avg_model_err", "Avg model err"),
                            true
                        )}
                        <th scope="col" className="text-right">
                            {translate("Manufacturing.ModelEvaluation.table.column.avg_norm_err", "Avg norm err")}
                        </th>
                        <th scope="col">{translate("Manufacturing.ModelEvaluation.models.open", "Open")}</th>
                    </tr>
                </thead>
                <tbody>
                    {props.rows.map(row => {
                        const ops = row.operations_count || 0;
                        return (
                            <tr key={`${row.plant_id}-${row.line_id}-${row.material_id}-${row.process_num}`}>
                                <td>{props.label_maps.plantsById.get(row.plant_id) ?? `Plant ${row.plant_id}`}</td>
                                <td>{props.label_maps.linesById.get(row.line_id) ?? `Line ${row.line_id}`}</td>
                                <td>{formatMaterialLabel(row)}</td>
                                <td className="text-right">{row.process_num}</td>
                                <td className="text-right">{ops.toLocaleString()}</td>
                                <td className="text-right">{formatDurationHours(row.total_duration_hours)}</td>
                                <td className="text-right">{formatCountWithPct(row.model_win_count, ops)}</td>
                                <td className="text-right">{formatCountWithPct(row.normative_win_count, ops)}</td>
                                <td className="text-right">{formatCountWithPct(row.fallback_count ?? 0, ops)}</td>
                                <td className="text-center align-middle py-2">
                                    <WinMixBar
                                        model_wins={row.model_win_count}
                                        norm_wins={row.normative_win_count}
                                        fallback_count={row.fallback_count ?? 0}
                                    />
                                </td>
                                <td className="text-right">{formatErrorRatioPct(row.avg_model_error_ratio_pct)}</td>
                                <td className="text-right">{formatErrorRatioPct(row.avg_normative_error_ratio_pct)}</td>
                                <td>
                                    {(() => {
                                        const open_url = buildModelBrowserUrl(
                                            resolveModelBrowserExternalIds(row, props.line_external_id_by_line_id)
                                        );
                                        return (
                                            <SimpleLink
                                                text={translate("Manufacturing.ModelEvaluation.models.open", "Open")}
                                                url={open_url ?? ""}
                                                tooltip={translate(
                                                    "Manufacturing.ModelEvaluation.models.open_hint",
                                                    "Open in Models browser"
                                                )}
                                                is_active={open_url != null}
                                            />
                                        );
                                    })()}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </Table>
        </div>
    );
}

export default function ModelsTab(props: Props) {
    const [lines, setLines] = useState<ILineData[]>([]);
    const [lines_ready, setLinesReady] = useState(false);
    const [applied_filters, setAppliedFilters] = useState<ModelEvaluationModelsFiltersState>(() =>
        mergeHandoffIntoModelsFilters(DEFAULT_MODEL_EVALUATION_MODELS_TAB_FILTERS, props.handoffFilters)
    );
    const [material_input, setMaterialInput] = useState("");
    const [sort, setSort] = useState<ModelEvaluationModelsSort>(DEFAULT_MODEL_EVALUATION_MODELS_SORT);
    const [page_index, setPageIndex] = useState(0);
    const [page_size, setPageSize] = useState(DEFAULT_MODEL_EVALUATION_MODELS_PAGE_SIZE);
    const [rows, setRows] = useState<IModelEvaluationKpiModelRow[]>([]);
    const [total_rows, setTotalRows] = useState(0);
    const [loading, setLoading] = useState(true);
    const [export_loading, setExportLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [job_uuid_mismatch, setJobUuidMismatch] = useState(false);

    const load_cancelled_ref = useRef(false);

    const label_maps = useMemo(
        () => buildEntityLabelMaps(props.aggregations, getPlantsForUser(), getLineGroupsForUser(), lines),
        [props.aggregations, lines]
    );

    const line_external_id_by_line_id = useMemo(() => new Map(lines.map(line => [line.id, line.external_id])), [lines]);

    const line_options = useMemo(() => {
        const options: { id: number; label: string }[] = [];
        const seen = new Set<number>();
        for (const plant of props.aggregations.plants) {
            if (applied_filters.plant_id != null && plant.id !== applied_filters.plant_id) {
                continue;
            }
            for (const line_group of plant.line_groups) {
                for (const line of line_group.lines) {
                    if (seen.has(line.id)) {
                        continue;
                    }
                    seen.add(line.id);
                    options.push({
                        id: line.id,
                        label: label_maps.linesById.get(line.id) ?? `Line ${line.id}`,
                    });
                }
            }
        }
        options.sort((left, right) => left.label.localeCompare(right.label));
        return options;
    }, [props.aggregations.plants, applied_filters.plant_id, label_maps.linesById]);

    useEffect(() => {
        let cancelled = false;
        const load_lines = async () => {
            await waitForManufacturingAsync();
            if (cancelled) {
                return;
            }
            try {
                const res = await getBackend2().manufacturing.getLines({});
                if (!cancelled) {
                    setLines(res.lines);
                    setLinesReady(true);
                }
            } catch (err: unknown) {
                if (!cancelled) {
                    console.error((err as Error).message);
                    setLinesReady(true);
                }
            }
        };
        void load_lines();
        return () => {
            cancelled = true;
        };
    }, []);

    useEffect(() => {
        if (props.handoffNonce === 0) {
            return;
        }
        setAppliedFilters(current => mergeHandoffIntoModelsFilters(current, props.handoffFilters));
        setPageIndex(0);
    }, [props.handoffNonce, props.handoffFilters]);

    useEffect(() => {
        if (!lines_ready) {
            return;
        }

        load_cancelled_ref.current = false;
        setLoading(true);
        setError(null);
        setJobUuidMismatch(false);

        const load = async () => {
            try {
                const req = buildModelsQueryRequest(props.window, applied_filters, sort, page_index + 1, page_size);
                const res = await fetchModelsPage(req);
                if (load_cancelled_ref.current) {
                    return;
                }
                const clamped_index = clampModelsPageIndex(page_index, res.pagination.total_rows, page_size);
                if (clamped_index !== page_index) {
                    setPageIndex(clamped_index);
                    return;
                }
                setRows(res.rows);
                setTotalRows(res.pagination.total_rows);
                setJobUuidMismatch(
                    modelEvaluationJobUuidMismatch(props.aggregations.metadata.job_uuid, res.metadata.job_uuid)
                );
            } catch (err: unknown) {
                if (!load_cancelled_ref.current) {
                    console.error((err as Error).message);
                    setError(translate("common.error"));
                    setRows([]);
                    setTotalRows(0);
                    setJobUuidMismatch(false);
                }
            } finally {
                if (!load_cancelled_ref.current) {
                    setLoading(false);
                }
            }
        };

        void load();
        return () => {
            load_cancelled_ref.current = true;
        };
    }, [props.window, applied_filters, sort, page_index, page_size, lines_ready, props.aggregations.metadata.job_uuid]);

    const applyPlantFilter = useCallback(
        (plant_id: number | null) => {
            setAppliedFilters(current => {
                let line_id = current.line_id;
                if (plant_id != null && line_id != null) {
                    const plant = props.aggregations.plants.find(p => p.id === plant_id);
                    const line_in_plant =
                        plant?.line_groups.some(lg => lg.lines.some(line => line.id === line_id)) ?? false;
                    if (!line_in_plant) {
                        line_id = null;
                    }
                }
                return {
                    ...current,
                    plant_id,
                    line_id: plant_id == null ? null : line_id,
                };
            });
            setPageIndex(0);
        },
        [props.aggregations.plants]
    );

    const applyLineFilter = useCallback((line_id: number | null) => {
        setAppliedFilters(current => ({
            ...current,
            line_id,
        }));
        setPageIndex(0);
    }, []);

    const applyMaterialFilter = useCallback((value: string) => {
        setMaterialInput(value);
        setAppliedFilters(current => ({
            ...current,
            material_external_id: normalizeMaterialExternalIdInput(value),
        }));
        setPageIndex(0);
    }, []);

    const applyActiveOnly = useCallback((active_only: boolean) => {
        setAppliedFilters(current => ({
            ...current,
            active_only,
        }));
        setPageIndex(0);
    }, []);

    const handleClearFilters = useCallback(() => {
        setAppliedFilters(DEFAULT_MODEL_EVALUATION_MODELS_TAB_FILTERS);
        setMaterialInput("");
        setPageIndex(0);
    }, []);

    const handleExportCsv = useCallback(async () => {
        setExportLoading(true);
        try {
            const req = buildModelsExportRequest(props.window, applied_filters, sort);
            const csv_rows = await exportModelsCsv(req);
            const date_stamp = new Date().toISOString().slice(0, 10);
            createCsvAndDownload(csv_rows, `model-evaluation-models-${props.window}-${date_stamp}.csv`);
        } catch (err: unknown) {
            console.error((err as Error).message);
            setError(translate("common.error"));
        } finally {
            setExportLoading(false);
        }
    }, [props.window, applied_filters, sort]);

    const page_count = total_rows > 0 ? Math.ceil(total_rows / page_size) : 0;
    const row_start = total_rows > 0 ? page_index * page_size + 1 : 0;
    const row_end = Math.min((page_index + 1) * page_size, total_rows);

    const can_clear = !modelsFiltersAreDefault(applied_filters);

    return (
        <div className="models-tab">
            <ModelEvaluationFilterBar
                footer={
                    total_rows > 0
                        ? `${translate(
                              "Manufacturing.ModelEvaluation.models.result_summary_prefix",
                              "Showing"
                          )} ${row_start.toLocaleString()}–${row_end.toLocaleString()} ${translate(
                              "Manufacturing.ModelEvaluation.models.result_summary_of",
                              "of"
                          )} ${total_rows.toLocaleString()} ${translate(
                              "Manufacturing.ModelEvaluation.models.result_summary_suffix",
                              "models"
                          )}`
                        : translate("Manufacturing.ModelEvaluation.models.result_summary_empty", "Showing 0 models")
                }
                footerAriaLive="polite"
            >
                <div className="form-row align-items-end">
                    <div className="form-group col-xl-2 col-lg-3 col-md-6 mb-2 mb-xl-0">
                        <Form.Label className="mb-1" htmlFor="models-filter-plant">
                            {translate("Manufacturing.ModelEvaluation.table.column.plant", "Plant")}
                        </Form.Label>
                        <Form.Control
                            id="models-filter-plant"
                            as="select"
                            size="sm"
                            value={applied_filters.plant_id ?? ""}
                            onChange={event => {
                                const value = event.target.value;
                                applyPlantFilter(value === "" ? null : Number(value));
                            }}
                        >
                            <option value="">
                                {translate("Manufacturing.ModelEvaluation.models.filter.all_plants", "All plants")}
                            </option>
                            {props.aggregations.plants.map(plant => (
                                <option key={plant.id} value={plant.id}>
                                    {label_maps.plantsById.get(plant.id) ?? `Plant ${plant.id}`}
                                </option>
                            ))}
                        </Form.Control>
                    </div>
                    <div className="form-group col-xl-3 col-lg-4 col-md-6 mb-2 mb-xl-0">
                        <Form.Label className="mb-1" htmlFor="models-filter-line">
                            {translate("Manufacturing.ModelEvaluation.table.column.line", "Line")}
                        </Form.Label>
                        <Form.Control
                            id="models-filter-line"
                            as="select"
                            size="sm"
                            value={applied_filters.line_id ?? ""}
                            onChange={event => {
                                const value = event.target.value;
                                applyLineFilter(value === "" ? null : Number(value));
                            }}
                        >
                            <option value="">
                                {translate("Manufacturing.ModelEvaluation.models.filter.all_lines", "All lines")}
                            </option>
                            {line_options.map(line => (
                                <option key={line.id} value={line.id}>
                                    {line.label}
                                </option>
                            ))}
                        </Form.Control>
                    </div>
                    <div className="form-group col-xl-2 col-lg-3 col-md-6 mb-2 mb-xl-0">
                        <Form.Label className="mb-1" htmlFor="models-filter-material">
                            {translate("Manufacturing.ModelEvaluation.models.filter.material", "Material external ID")}
                        </Form.Label>
                        <Form.Control
                            id="models-filter-material"
                            type="text"
                            size="sm"
                            placeholder={translate(
                                "Manufacturing.ModelEvaluation.models.filter.material_placeholder",
                                "Exact material external ID"
                            )}
                            value={material_input}
                            onChange={event => {
                                applyMaterialFilter(event.target.value);
                            }}
                        />
                    </div>
                    <div className="form-group col-xl-2 col-lg-4 col-md-6 mb-2 mb-xl-0">
                        <div className="custom-control custom-checkbox mt-2 pt-1">
                            <input
                                type="checkbox"
                                className="custom-control-input"
                                id="models-filter-active-only"
                                checked={applied_filters.active_only}
                                onChange={event => {
                                    applyActiveOnly(event.target.checked);
                                }}
                            />
                            <label className="custom-control-label small" htmlFor="models-filter-active-only">
                                {translate(
                                    "Manufacturing.ModelEvaluation.models.filter.active_only",
                                    "Active models only"
                                )}
                            </label>
                        </div>
                    </div>
                    <div className="form-group col-xl-3 col-lg-12 col-md-12 mb-0 d-flex align-items-end flex-wrap justify-content-end">
                        <Button
                            variant="outline-secondary"
                            size="sm"
                            className="mr-2 mb-0"
                            disabled={!can_clear}
                            onClick={handleClearFilters}
                        >
                            {translate("Manufacturing.ModelEvaluation.models.clear_filter", "Clear filter")}
                        </Button>
                        <Button
                            variant="primary"
                            size="sm"
                            className="mb-0"
                            disabled={loading || export_loading}
                            onClick={() => {
                                void handleExportCsv();
                            }}
                        >
                            {export_loading
                                ? translate("common.loading", "Loading...")
                                : translate("Manufacturing.ModelEvaluation.models.export_csv", "Export CSV")}
                        </Button>
                    </div>
                </div>
            </ModelEvaluationFilterBar>

            {error != null && <ErrorComponent msg={error} type="error" className="mb-3" />}
            {job_uuid_mismatch && (
                <ErrorComponent
                    msg={translate(
                        "Manufacturing.ModelEvaluation.job_uuid_mismatch",
                        "Warning: aggregations and models reports are from different preparation runs."
                    )}
                    type="warning"
                    className="mb-3"
                />
            )}

            <div className="card p-0 mb-0">
                {loading ? (
                    <div className="px-3 py-5">
                        <Loader />
                    </div>
                ) : rows.length > 0 ? (
                    <>
                        <ModelsTable
                            rows={rows}
                            sort={sort}
                            label_maps={label_maps}
                            line_external_id_by_line_id={line_external_id_by_line_id}
                            onSortChange={setSort}
                        />
                        <div className="d-flex flex-wrap align-items-center justify-content-between px-3 py-2 border-top bg-light">
                            <Pagination
                                num_rows={total_rows}
                                can_previous_page={page_index > 0}
                                can_next_page={page_index < page_count - 1}
                                page_options={Array.from({ length: page_count }, (_, index) => index)}
                                page_count={page_count}
                                page_index={page_index}
                                page_size={page_size}
                                page_sizes={PAGE_SIZES}
                                gotoPage={setPageIndex}
                                nextPage={() => {
                                    setPageIndex(current => current + 1);
                                }}
                                previousPage={() => {
                                    setPageIndex(current => current - 1);
                                }}
                                setPageSize={new_size => {
                                    setPageSize(new_size);
                                    setPageIndex(0);
                                }}
                            />
                        </div>
                    </>
                ) : (
                    <p className="text-muted px-3 py-4 mb-0">{translate("common.no_data")}</p>
                )}
            </div>
        </div>
    );
}
